import React from "react";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import { useLang } from "@/lib/i18n";

const TEAM_IMAGES = [
  { name: "Cengiz Kosan", image: "https://customer-assets.emergentagent.com/job_ai-optimize-9/artifacts/5uzcopun_330b4fd9-dcc5-44df-903c-d8d9f42ad1be.jpeg", linkedin: "https://linkedin.com" },
  { name: "Martina Gallo", image: "https://customer-assets.emergentagent.com/job_ai-optimize-9/artifacts/lypypi8m_ChatGPT%20Image%204.%20Juni%202026%2C%2014_47_03.png", linkedin: "https://linkedin.com" },
  { name: "Yasemin Erdal", image: "https://customer-assets.emergentagent.com/job_ai-optimize-9/artifacts/ilsiv3j1_ChatGPT%20Image%204.%20Juni%202026%2C%2021_27_18.png", linkedin: "https://linkedin.com" },
];

export default function Team() {
  const { t, dict } = useLang();
  const members = dict.team.members.map((m, i) => ({ ...m, ...TEAM_IMAGES[i] }));

  return (
    <section id="team" data-testid="team-section" className="relative py-14 lg:py-20 bg-[#0A192F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-1 gap-10 mb-16">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#93C5FD]">{t("team.eyebrow")}</span>
            <h2 className="mt-6 font-heading font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] whitespace-pre-line lg:whitespace-normal">
              {t("team.title")}
            </h2>
            <p className="mt-6 text-base text-slate-300 leading-relaxed">{t("team.lead")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:max-w-5xl lg:mx-auto">
          {members.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`team-card-${i}`}
              className="group"
            >
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-slate-800 mb-5 ring-1 ring-white/10">
                <img
                  src={m.image}
                  alt={m.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {m.joining && (
                  <div
                    data-testid={`team-badge-${i}`}
                    className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#E91E63] text-white text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 shadow-lg ring-1 ring-white/20"
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    {m.joining}
                  </div>
                )}
                <a
                  href={m.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`team-linkedin-${i}`}
                  className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white text-[#0A192F] hover:bg-[#0A66C2] hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500"
                  aria-label={`LinkedIn ${m.name}`}
                >
                  <Linkedin size={18} />
                </a>
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-[#E91E63]">{m.name}</h3>
                <div className="text-xs uppercase tracking-[0.18em] text-[#93C5FD] font-semibold mt-1">{m.role}</div>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{m.bio}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
